import { Component } from '@theme/component';
import { onDocumentLoaded, changeMetaThemeColor, setHeaderMenuStyle } from '@theme/utilities';

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer - The header drawer container element
 * @property {HTMLElement} headerMenu - The header menu element
 * @property {HTMLElement} headerRowTop - The header top row element
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */

class HeaderComponent extends Component {
  requiredRefs = ['headerDrawerContainer', 'headerMenu', 'headerRowTop'];

  /**
   * Width of window when header drawer was hidden
   * @type {number | null}
   */
  #menuDrawerHiddenWidth = null;

  /**
   * An intersection observer for monitoring sticky header position
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /**
   * Whether the header has been scrolled offscreen, when sticky behavior is 'scroll-up'
   * @type {boolean}
   */
  #offscreen = false;

  /**
   * The last recorded scrollTop of the document, when sticky behavior is 'scroll-up
   * @type {number}
   */
  #lastScrollTop = 0;

  /**
   * A timeout to allow for hiding animation, when sticky behavior is 'scroll-up'
   * @type {number | null}
   */
  #timeout = null;

  /**
   * RAF ID for scroll handler throttling
   * @type {number | null}
   */
  #scrollRafId = null;

  /**
   * Keeps the global `--header-height` custom property up to date,
   * which other theme components can then consume
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry || !entry.borderBoxSize[0]) return;

    // The initial height is calculated using the .offsetHeight property, which returns an integer.
    // We round to the nearest integer to avoid unnecessaary reflows.
    const roundedHeaderHeight = Math.round(entry.borderBoxSize[0].blockSize);
    document.body.style.setProperty('--header-height', `${roundedHeaderHeight}px`);

    // Check if the menu drawer should be hidden in favor of the header menu
    if (this.#menuDrawerHiddenWidth && window.innerWidth > this.#menuDrawerHiddenWidth) {
      this.#updateMenuVisibility(false);
    }
  });

  /**
   * Observes the header while scrolling the viewport to track when its actively sticky
   * @param {Boolean} alwaysSticky - Determines if we need to observe when the header is offscreen
   */
  #observeStickyPosition = (alwaysSticky = true) => {
    if (this.#intersectionObserver) return;

    const config = {
      threshold: alwaysSticky ? 1 : 0,
    };

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const { isIntersecting } = entry;

      if (alwaysSticky) {
        this.dataset.stickyState = isIntersecting ? 'inactive' : 'active';
        if (this.dataset.themeColor) changeMetaThemeColor(this.dataset.themeColor);
      } else {
        this.#offscreen = !isIntersecting || this.dataset.stickyState === 'active';
      }
    }, config);

    this.#intersectionObserver.observe(this);
  };

  /**
   * Handles the overflow minimum event from the header menu
   * @param {OverflowMinimumEvent} event
   */
  #handleOverflowMinimum = (event) => {
    this.#updateMenuVisibility(event.detail.minimumReached);
  };

  /**
   * Updates the visibility of the menu and drawer
   * @param {boolean} hideMenu - Whether to hide the menu and show the drawer
   */
  #updateMenuVisibility(hideMenu) {
    if (hideMenu) {
      this.#menuDrawerHiddenWidth = window.innerWidth;
    } else {
      this.#menuDrawerHiddenWidth = null;
    }
    setHeaderMenuStyle();
  }

  #handleWindowScroll = () => {
    if (this.#scrollRafId !== null) return;

    this.#scrollRafId = requestAnimationFrame(() => {
      this.#scrollRafId = null;
      this.#updateScrollState();
    });
  };

  #updateScrollState = () => {
    const stickyMode = this.getAttribute('sticky');
    if (!this.#offscreen && stickyMode !== 'always') return;

    const scrollTop = document.scrollingElement?.scrollTop ?? 0;
    const headerTop = this.getBoundingClientRect().top;
    const isScrollingUp = scrollTop < this.#lastScrollTop;
    const isAtTop = headerTop >= 0;

    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    if (stickyMode === 'always') {
      if (isAtTop) {
        this.dataset.scrollDirection = 'none';
      } else if (isScrollingUp) {
        this.dataset.scrollDirection = 'up';
      } else {
        this.dataset.scrollDirection = 'down';
      }

      this.#lastScrollTop = scrollTop;
      return;
    }

    if (isScrollingUp) {
      if (isAtTop) {
        // reset sticky state when header is scrolled up to natural position
        this.#offscreen = false;
        this.dataset.stickyState = 'inactive';
        this.dataset.scrollDirection = 'none';
      } else {
        // show sticky header when scrolling up
        this.dataset.stickyState = 'active';
        this.dataset.scrollDirection = 'up';
      }
    } else if (this.dataset.stickyState === 'active') {
      this.dataset.scrollDirection = 'none';

      this.dataset.stickyState = 'idle';
    } else {
      this.dataset.scrollDirection = 'none';
      this.dataset.stickyState = 'idle';
    }

    this.#lastScrollTop = scrollTop;
  };

  connectedCallback() {
    super.connectedCallback();
    this.#resizeObserver.observe(this);
    this.addEventListener('overflowMinimum', this.#handleOverflowMinimum);

    const stickyMode = this.getAttribute('sticky');
    if (stickyMode) {
      this.#observeStickyPosition(stickyMode === 'always');

      if (stickyMode === 'scroll-up' || stickyMode === 'always') {
        document.addEventListener('scroll', this.#handleWindowScroll);
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    this.removeEventListener('overflowMinimum', this.#handleOverflowMinimum);
    document.removeEventListener('scroll', this.#handleWindowScroll);
    if (this.#scrollRafId !== null) {
      cancelAnimationFrame(this.#scrollRafId);
      this.#scrollRafId = null;
    }
    document.body.style.setProperty('--header-height', '0px');
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

onDocumentLoaded(() => {
  const header = document.querySelector('header-component');
  const headerGroup = document.querySelector('#header-group');

  // Note: Initial header heights are set via inline script in theme.liquid
  // This ResizeObserver handles dynamic updates after page load

  // Update header group height on resize of any child
  if (headerGroup) {
    const resizeObserver = new ResizeObserver((entries) => {
      const headerGroupHeight = entries.reduce((totalHeight, entry) => {
        if (
          entry.target !== header ||
          (header.hasAttribute('transparent') && header.parentElement?.nextElementSibling)
        ) {
          return totalHeight + (entry.borderBoxSize[0]?.blockSize ?? 0);
        }
        return totalHeight;
      }, 0);
      // The initial height is calculated using the .offsetHeight property, which returns an integer.
      // We round to the nearest integer to avoid unnecessaary reflows.
      const roundedHeaderGroupHeight = Math.round(headerGroupHeight);
      document.body.style.setProperty('--header-group-height', `${roundedHeaderGroupHeight}px`);
    });

    if (header instanceof HTMLElement) {
      resizeObserver.observe(header);
    }

    // Observe all children of the header group
    const children = headerGroup.children;
    for (let i = 0; i < children.length; i++) {
      const element = children[i];
      if (element instanceof HTMLElement) {
        resizeObserver.observe(element);
      }
    }

    // Also observe the header group itself for child changes
    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          // Re-observe all children when the list changes
          const children = headerGroup.children;
          for (let i = 0; i < children.length; i++) {
            const element = children[i];
            if (element instanceof HTMLElement) {
              resizeObserver.observe(element);
            }
          }
        }
      }
    });

    mutationObserver.observe(headerGroup, { childList: true });
  }
});

document.addEventListener('DOMContentLoaded', () => {
  const popup = document.querySelector('.softlore-email-popup');
  if (!popup) return;

  const heading = popup.querySelector('.softlore-email-popup__dialog h2');
  const body = popup.querySelector('.softlore-email-popup__body');
  const button = popup.querySelector('.softlore-email-popup__form button[type="submit"]');
  const note = popup.querySelector('.softlore-email-popup__note');
  const form = popup.querySelector('.softlore-email-popup__form');
  const tags = form?.querySelector('input[name="contact[tags]"]');

  if (heading) heading.textContent = 'Join for a first-order gift';
  if (body) {
    body.textContent =
      'Leave your email for drop notes and studio updates. When you place your first independent-shop order, we will include one small random handmade accessory in your parcel.';
  }
  if (button) button.textContent = 'Get the gift note';
  if (note) note.textContent = 'By joining, you agree to receive SoftLore emails. You can unsubscribe anytime.';
  if (tags) tags.value = 'newsletter, softlore-popup, first-order-gift';

  if (!popup.querySelector('.softlore-email-popup__gift-card')) {
    if (!document.getElementById('softlore-email-popup-gift-fallback-style')) {
      const style = document.createElement('style');
      style.id = 'softlore-email-popup-gift-fallback-style';
      style.textContent =
        '.softlore-email-popup__intro{display:grid;grid-template-columns:minmax(0,1fr)132px;gap:16px;align-items:start;margin-bottom:18px}.softlore-email-popup__copy{display:grid;gap:10px}.softlore-email-popup__gift-card{display:grid;grid-template-columns:1fr 1fr;gap:6px;border:1px solid rgb(0 0 0 / 10%);border-radius:8px;padding:6px;background:#fff;box-shadow:0 10px 30px rgb(0 0 0 / 10%);transform:rotate(1.25deg)}.softlore-email-popup__gift-card img{width:100%;aspect-ratio:1;height:auto;border-radius:5px;object-fit:cover}.softlore-email-popup__gift-card img:first-child{grid-column:1/-1}.softlore-email-popup__gift-note{margin:0;font-size:.82rem;line-height:1.45;opacity:.72}@media screen and (max-width:749px){.softlore-email-popup__intro{grid-template-columns:1fr;gap:12px}.softlore-email-popup__gift-card{grid-template-columns:repeat(3,1fr);transform:none}.softlore-email-popup__gift-card img:first-child{grid-column:auto}}';
      document.head.appendChild(style);
    }

    const giftNote = document.createElement('p');
    giftNote.className = 'softlore-email-popup__gift-note';
    giftNote.textContent = 'Photos show examples only. The actual accessory is random and may be different from these three.';

    const giftCard = document.createElement('div');
    giftCard.className = 'softlore-email-popup__gift-card';
    giftCard.setAttribute('aria-label', 'Examples of possible random small accessories');

    ['1', '2', '3'].forEach((index) => {
      const image = document.createElement('img');
      image.src = `/cdn/shop/t/8/assets/softlore-random-gift-${index}.webp`;
      image.alt = 'Example random handmade accessory';
      image.loading = 'lazy';
      image.width = 160;
      image.height = 160;
      giftCard.appendChild(image);
    });

    const intro = document.createElement('div');
    intro.className = 'softlore-email-popup__intro';
    const copy = document.createElement('div');
    copy.className = 'softlore-email-popup__copy';
    body?.replaceWith(intro);
    if (body) copy.appendChild(body);
    copy.appendChild(giftNote);
    intro.appendChild(copy);
    intro.appendChild(giftCard);
  }

  if (form && !form.querySelector('input[name="contact[accepts_marketing]"]')) {
    const acceptsMarketing = document.createElement('input');
    acceptsMarketing.type = 'hidden';
    acceptsMarketing.name = 'contact[accepts_marketing]';
    acceptsMarketing.value = 'true';
    form.appendChild(acceptsMarketing);
  }

  if (form && !form.querySelector('.softlore-email-popup__submit-message')) {
    const submitMessage = document.createElement('p');
    submitMessage.className = 'softlore-email-popup__submit-message';
    submitMessage.setAttribute('role', 'status');
    submitMessage.setAttribute('aria-live', 'polite');
    submitMessage.textContent = '';
    form.appendChild(submitMessage);
  }

  if (form && button && form.dataset.softloreAsyncSubmit !== 'true') {
    form.dataset.softloreAsyncSubmit = 'true';
    form.addEventListener('submit', async (event) => {
      event.preventDefault();

      const originalButtonText = button.textContent || 'Get the gift note';
      const submitMessage = form.querySelector('.softlore-email-popup__submit-message');
      const controller = new AbortController();
      const timeout = window.setTimeout(() => controller.abort(), 12000);

      button.textContent = 'Joining...';
      button.setAttribute('aria-busy', 'true');
      button.disabled = true;
      if (submitMessage) submitMessage.textContent = 'Joining. Please wait...';

      try {
        const response = await fetch(form.action, {
          method: 'POST',
          body: new FormData(form),
          credentials: 'same-origin',
          redirect: 'follow',
          signal: controller.signal,
        });
        const responseUrl = new URL(response.url, window.location.origin);
        const responseText = await response.text();
        const posted =
          responseUrl.searchParams.has('customer_posted') ||
          responseText.includes('customer_posted=true') ||
          responseText.includes('You are on the list. We will remember your first-order gift note.');

        if (!response.ok || !posted) throw new Error('Email signup was not confirmed.');

        localStorage.setItem('softlore-email-popup-joined', '1');
        form.reset();
        button.textContent = 'You are on the list';
        if (submitMessage) submitMessage.textContent = 'Success. Your first-order gift note is saved.';
        window.setTimeout(() => {
          const dialog = popup.querySelector('dialog');
          if (dialog?.open) dialog.close();
          popup.hidden = true;
        }, 1400);
      } catch (error) {
        button.textContent = originalButtonText;
        button.removeAttribute('aria-busy');
        button.disabled = false;
        if (submitMessage) {
          submitMessage.textContent =
            error?.name === 'AbortError'
              ? 'This is taking too long. Please try again.'
              : 'We could not add your email. Please try again.';
        }
      } finally {
        window.clearTimeout(timeout);
      }
    });
  }
});
